import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getConsentStatus, recordConsent, type ConsentChannel, type ConsentPurpose } from "@/services/api";

const consentStatusKey = (userId: string | undefined) => ["consent-status", userId] as const;

/**
 * The signed-in user's consent position (needs to accept a new Terms /
 * Privacy version? marketing opt-in?). Fails open — see getConsentStatus.
 */
export function useConsentStatus({ enabled = true }: { enabled?: boolean } = {}) {
  const { session, user } = useAuth();
  const accessToken = session?.access_token;
  return useQuery({
    queryKey: consentStatusKey(user?.id),
    queryFn: () => getConsentStatus(accessToken!),
    enabled: enabled && !!accessToken && !!user?.id,
    staleTime: 5 * 60_000,
  });
}

/** Records a consent event for the signed-in user, then refreshes the status. */
export function useRecordConsent() {
  const { session, user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { purpose: ConsentPurpose; channel: ConsentChannel; marketing?: boolean }) => {
      if (!session?.access_token) throw new Error("Not signed in");
      const result = await recordConsent({ ...input, accessToken: session.access_token });
      if (!result.success) throw new Error("Consent could not be saved");
      return result;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: consentStatusKey(user?.id) }),
  });
}
